import { Box, FormGroup, MenuItem, TextField } from '@mui/material';
import { useField } from 'formik';
import TextWrapper from './useTextWrapper';
import InputWrapper from './useInput';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';

SelectWrapper.propTypes = {
	placeholder: PropTypes.any,
	items: PropTypes.any,
	valueKey: PropTypes.any,
	labelKey: PropTypes.any,
	controlOtherValueName: PropTypes.any,
	delimiter: PropTypes.string,
	classList: PropTypes.string,
	wrapperClass: PropTypes.string,
	preventSetValue: PropTypes.bool,
	isSmallPopup: PropTypes.bool,
	onChanges: PropTypes.func,
};

export default function SelectWrapper({
	placeholder,
	items = [],
	valueKey = 'id',
	labelKey = 'displayName',
	controlOtherValueName = '',
	delimiter = ' ',
	classList = '',
	wrapperClass = '',
	labelClass = '',
	preventSetValue = false,
	isSmallPopup = false,
	isSearchable = true,
	searchableKey = '',
	multiSelect = false,
	onChanges = (e) => { },
	onBlur,
	showDefaultOption = false,
	defaultOptionText = 'default',
	defaultOptionValue = 'default',
	valueSpecifiedNames = ['Other'],
	...props
}) {
	const [field, meta, helper] = useField(props);
	const [searchTerm, setSearchTerm] = useState('');
	const [filteredItems, setFilteredItems] = useState(items);

	useEffect(() => {
		setFilteredItems(items);
	}, [items]);

	const getValue = (option) => {
		if (typeof option !== 'object' || !option) {
			return option;
		}

		if (option[valueKey] === 0) {
			option[valueKey] = '0';
		}
		return option[valueKey];
	};

	const displayLabel = (option) => {
		if (typeof option !== 'object' || !option) {
			return option;
		}
		if (typeof labelKey === 'string') {
			return option[labelKey];
		}
		return labelKey.map((key) => option[key]).join(delimiter);
	};

	const hasOtherValue = () => {
		return controlOtherValueName && isOther(field.value);
	};

	const isOther = (value) => {
		if (!value) {
			return false;
		}
		let filtered = filteredItems?.find((resp) => resp?.id === value);
		return !!valueSpecifiedNames.find(value => value.toLocaleLowerCase().replace(/\s+/g, '') === filtered?.name?.toLocaleLowerCase().replace(/\s+/g, ''));
	};

	const onSearchValue = (value = '') => {
		if (!isSearchable) {
			return;
		}
		setSearchTerm(value);

		if (!value) {
			setFilteredItems(items);
			return;
		}

		let searchValue = value?.toLocaleLowerCase();
		setFilteredItems(
			items.filter((res) =>
				res[searchableKey ? searchableKey : labelKey]
					?.toLocaleLowerCase()
					?.replace(/\s/g, '')
					?.includes(searchValue?.replace(/\s/g, ''))
			)
		);
	};

	return (
		<FormGroup
			className="w-full"
			style={{ height: 'fit-content' }}>
			<Box
				className={
					hasOtherValue() ? 'grid grid-cols-2 gap-2 w-full' : 'w-full'
				}>
				<Box
					className={`flex flex-col flex-0 items-start w-full ${wrapperClass}`}>
					<Box className="flex flex-0 items-center w-full">
						<TextWrapper
							classList={`${labelClass} truncate-word !text-[11px]`}
							type="caption"
							text={meta.error && meta.touched && props?.label ? `${props?.label} ` : props?.label}
						/>
						{meta.error && meta.touched && props?.label ? (
							<span className="!text-red-500 !text-[11px]">*</span>
						) : null}
					</Box>
					<TextField
						onChange={(e) => {
							let value = e.target.value;
							if (!preventSetValue) {
								helper.setValue(value);
							}
							onChanges(value);
						}}
						onBlur={(e) => {
							field.onBlur(e);
							onBlur && onBlur(e.target.value);
						}}
						error={meta.error && meta.touched ? true : false}
						helperText={<span>{meta.touched ? meta.error : ''}</span>}
						defaultValue={null}
						variant={'outlined'}
						SelectProps={{
							multiple: multiSelect,
							style: { textAlign: 'start' },
							onClose: () => {
								onSearchValue('');
							},
							onOpen: () => {
								setTimeout(() => {
									document.getElementById(`input-${field.name}`)?.focus();
									onSearchValue('');
								});
							},
						}}
						InputLabelProps={{ shrink: false, style: { display: 'none' } }}
						size="small"
						select
						fullWidth={true}
						required={props?.isRequired ? true : false}
						name={field?.name}
						value={field?.value}
						className={props?.classList}
						disabled={props?.disabled || false}>
						{isSearchable ? (
							<Box onKeyDown={(e) => e.stopPropagation()}>
								<TextField
									id={`input-${field.name}`}
									onChange={(e) => {
										onSearchValue(e.target.value);
										setTimeout(() =>
											document.getElementById(`input-${field.name}`).focus()
										);
									}}
									value={searchTerm}
									InputProps={{
										endAdornment: searchTerm ? (
											<CloseIcon
												onClick={() => {
													onSearchValue('');
												}}
												className="text-[#5b5b5b] cursor-pointer"></CloseIcon>
										) : null,
									}}
									placeholder={'Search Here...'}
									variant="outlined"
									size="small"
									className="w-auto !mx-2 !mb-2 !flex"></TextField>
							</Box>
						) : null}
						<MenuItem
							onKeyDown={(e) => e.stopPropagation()}
							className={`!hidden`}
							value={''}
							disabled></MenuItem>
						{showDefaultOption && filteredItems.length ? (
							<MenuItem
								onKeyDown={(e) => e.stopPropagation()}
								className={`custom-menu ${isSmallPopup ? '!whitespace-break-spaces' : ''
									}`}
								value={defaultOptionValue}>
								{defaultOptionText}
							</MenuItem>
						) : null}
						{filteredItems && filteredItems.length > 0 ? (
							filteredItems.map((resp, index) => {
								return (
									<MenuItem
										onKeyDown={(e) => e.stopPropagation()}
										className={`${isSmallPopup
											? '!max-w-[400px] !whitespace-break-spaces'
											: ''
											}`}
										key={index}
										value={
											multiSelect ? getValue(resp).toString() : getValue(resp)
										}>
										{displayLabel(resp)}
									</MenuItem>
								);
							})
						) : (
							<MenuItem
								disabled={true}
								onKeyDown={(e) => e.stopPropagation()}>
								<em>Nothing to display</em>
							</MenuItem>
						)}
					</TextField>
				</Box>
				{hasOtherValue() ? (
					<Box>
						<InputWrapper
							label={field.value === 12 ? 'Influencer Name' : 'Other'}
							name={controlOtherValueName}
							key={controlOtherValueName}></InputWrapper>
					</Box>
				) : null}
			</Box>
		</FormGroup>
	);
}
